// The API's own default, repeated here so the field is populated before the
// user touches it. Keep in step with DEFAULT_ID_SIZE in the API's
// functions/segment-id-sets/generateSegmentId.
export const DEFAULT_SIZE = 16

// The API rejects anything outside this range; matching it here turns a failed
// round-trip into inline feedback. Keep in step with MIN_ID_SIZE/MAX_ID_SIZE in
// the API's functions/segment-id-sets/generateSegmentId.
export const MIN_SIZE = 6
export const MAX_SIZE = 32

export const INITIAL_FORM = {
  name: '',
  description: '',
  alphanumeric: true,
  starts_with_letter: true,
  size: String(DEFAULT_SIZE),
}

// Mirrors the API's validation so the message lands next to the field that
// caused it. The API validates again regardless - this is not the gate.
// Returns an error string, or null when the form is usable.
export const validate = ({ name, alphanumeric, starts_with_letter, size }) => {
  if (!String(name ?? '').trim()) return 'Name is required'

  // Number('') is 0 and Number('12abc') is NaN, so both fall out here rather
  // than reaching the API as a bad size
  const parsed = Number(size)
  if (!Number.isInteger(parsed) || parsed < MIN_SIZE || parsed > MAX_SIZE) {
    return `ID size must be a whole number between ${MIN_SIZE} and ${MAX_SIZE}`
  }

  // a digits-only ID has no letter to lead with
  if (starts_with_letter && !alphanumeric) {
    return 'Starts with letter requires Alphanumeric to be on'
  }

  return null
}

// Alphabet the API draws from. Uppercase only - single-case is deliberate there,
// so a partner that upper- or lower-cases IDs in transit cannot fold two
// distinct IDs into one.
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const DIGITS = '0123456789'

// Builds one example ID so the rules can be read at a glance before committing
// to them. This mirrors generateSegmentId in the API, but it is only ever shown
// - the value is never submitted, and the IDs that actually get delivered are
// drawn server-side from crypto.randomInt. Math.random is fine for an
// illustration and keeps the modal free of a crypto dependency.
//
// Returns '' when the size is not yet a usable number, so a half-typed value
// shows nothing rather than a misleading example.
export const generatePreviewId = ({ alphanumeric, starts_with_letter, size }) => {
  const length = Number(size)
  // Gate on the same range validate() enforces (MIN_SIZE..MAX_SIZE). A count of
  // 1-5 is a number, but the form would reject it, so previewing a clean example
  // beneath a "6-32 characters" field would only endorse a value Create refuses.
  if (!Number.isInteger(length) || length < MIN_SIZE || length > MAX_SIZE) return ''

  // a digits-only ID has no letter to lead with - the form rejects this
  // combination, so preview it as the digits-only ID it would have to be
  const chars = alphanumeric ? LETTERS + DIGITS : DIGITS
  const pick = (from) => from[Math.floor(Math.random() * from.length)]

  const out = starts_with_letter && alphanumeric ? [pick(LETTERS)] : []
  while (out.length < length) out.push(pick(chars))

  return out.join('')
}
